import { AggregateQuerySpec, DateFilterKey, DatePresetKey } from "../../../dashboard-framework/types";
import { BOOKED_STATUSES } from "../../../api/client-sessions-summary/constants";
import { FIRST_EEG_SERVICE, PSY_SERVICE } from "../../../utils/constants";

// Fórmulas extraídas do Zoho "Digital MKT Analysis [NEW]" em 2026-06-11 (ver spec.md).
// Widgets de custos (Google/Facebook Ads) NÃO são migráveis — fonte externa ao Directus.

export const DIGITAL_MARKETING_KEY = "digital-marketing";

export const TO_COME_STATUSES = BOOKED_STATUSES;

export const EVALUATIONS_STARTED_STATUSES = ["awaiting_in_clinic", "in_session", "done"];

export const leadsCountQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    dataScope: "global",
};

export const leadsMonthlyQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)"],
    dataScope: "global",
};

// Leads por clínica (chips por clínica no cartão "Leads hoje"). clinic: M2O clinics (1 Porto, 3 Braga).
export const leadsByClinicQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["clinic"],
    dataScope: "global",
};

// Leads por tipo de cliente (chips crianças/adultos de vigilância da segmentação das campanhas).
// client_type é uma coluna da própria leads, logo o groupBy é direto. Para SESSÕES não há equivalente:
// o tipo só existe na lead ligada e o Directus devolve 500 num groupBy relacional (lead.client_type),
// por isso os cartões de sessões buscam itens crus com lead.client_type e agregam no cliente.
export const leadsByClientTypeQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["client_type"],
    dataScope: "global",
};

export const evaluationsBookedByClinicQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["clinic_id"],
    fixedFilter: { service: { _eq: FIRST_EEG_SERVICE } },
    dataScope: "global",
};

// evaluationsCountQuery, reportsCountQuery, EVALUATIONS_HAPPENING_STATUSES and the goals of the two upcoming
// gauges live in ../shared/sharedMetrics.ts — they are shared with reception-kpis (see README, "Métricas partilhadas").

export const evaluationsDoneMonthlyQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(start_date)", "month(start_date)"],
    fixedFilter: { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, { status: { _eq: "done" } }] },
    dataScope: "global",
};

export const treatmentsStartedCountQuery: AggregateQuerySpec = {
    entity: "treatments",
    aggregate: { count: "*" },
    dataScope: "global",
};

export const treatmentsStartedMonthlyQuery: AggregateQuerySpec = {
    entity: "treatments",
    aggregate: { count: "*" },
    groupBy: ["year(date_of_start)", "month(date_of_start)"],
    dataScope: "global",
};

export const dailyLeadsQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)", "day(date_created)"],
    dataScope: "global",
};

// Leads criadas por dia E por clínica (gráfico "Leads qualificadas por dia" com filtro de datas próprio).
export const dailyLeadsByClinicQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)", "day(date_created)", "clinic"],
    dataScope: "global",
};

export const dailyFirstVisitBookingsQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)", "day(date_created)"],
    fixedFilter: { _and: [{ lead: { _nnull: true } }, { service: { _in: [FIRST_EEG_SERVICE, 34] } }] },
    dataScope: "global",
};

// Réplica EXATA da fórmula Zoho leads_qualified_categories (lida no designer 2026-07-14):
// if_case("status", 'repeated_lead','Bad lead', ..., 'call_1st_time','Pending contact',
//   'call_planed','Qualified', ..., 'all_good','Good lead', ..., 'Unknown').
// Qualquer estado não mapeado (no_show, "", null) → Unknown.
export const LEAD_QUALIFIED_CATEGORY_BY_STATUS: Record<string, string> = {
    repeated_lead: "Bad lead",
    wrong_city: "Bad lead",
    wrong_number: "Bad lead",
    no_answer_10_calls: "Bad lead",
    declined: "Bad lead",
    denied_submiting: "Bad lead",
    we_declined: "Bad lead",
    mistake_notrelated: "Bad lead",
    no_treatment: "Bad lead",
    call_1st_time: "Pending contact",
    call_planed: "Qualified",
    thinking: "Qualified",
    thinking_done: "Qualified",
    for_future: "Qualified",
    all_good: "Good lead",
    waiting_eeg: "Good lead",
    done: "Good lead",
};

export const BAD_LEAD_CATEGORY = "Bad lead";

// Ordem das séries na legenda (igual ao Zoho): Bad lead, Good lead, Pending contact, Qualified, Unknown.
export const QUALIFIED_CATEGORIES_ORDER = [BAD_LEAD_CATEGORY, "Good lead", "Pending contact", "Qualified", "Unknown"];

// LEAD QUALIFICADA = tudo o que NÃO cai em Bad lead (Good lead + Qualified + Pending contact + Unknown).
// É o denominador de TODOS os "custo por lead" do dashboard: o custo blended dividido por todas as leads
// era enganador nos dias com muitas leads lixo. Derivado do mapa acima para nunca divergir dele.
export const BAD_LEAD_STATUSES = Object.keys(LEAD_QUALIFIED_CATEGORY_BY_STATUS).filter(
    (status) => LEAD_QUALIFIED_CATEGORY_BY_STATUS[status] === BAD_LEAD_CATEGORY
);

// Filtro Directus das leads qualificadas. O `_or` com `_null` é OBRIGATÓRIO: em SQL um `status _nin [...]`
// sozinho descarta silenciosamente as leads sem status (verificado em produção: 12 em vez de 14 no mesmo dia),
// e essas leads ainda sem triagem contam como qualificadas (categoria Unknown).
export const QUALIFIED_LEAD_FILTER = { _or: [{ status: { _null: true } }, { status: { _nin: BAD_LEAD_STATUSES } }] };

// Lead marcada = tem pelo menos uma sessão associada (qualquer serviço, qualquer estado) — a mesma "marcação"
// dos gráficos de grupos de anúncios do Facebook.
export const BOOKED_LEAD_FILTER = { "count(sessions)": { _gt: 0 } };

export const LEADS_LAST_7_DAYS_GOAL = 140;
export const EVALUATIONS_DONE_30_DAYS_GOAL = 110;

export const TREATMENTS_NEXT_15_DAYS_TARGET = 40;

// Receita das avaliações = apenas Avaliação qEEG - Primeira (validado: svc39 112.106€/12m ≈ média Zoho).
export const EVALUATION_REVENUE_SERVICES = [FIRST_EEG_SERVICE];

// "Tratamentos a começar": o Zoho NÃO usa date_of_start; usa a query table "first session of
// treatment" — mesma semântica do "+ Por Vir" da Análise Diária. Métrica partilhada:
// treatmentsFirstSessionQuery em ../shared/sharedMetrics.ts.

// ────────────────────────────────────────────────────────────────────────────────────────────
// CUSTOS PPC (marketing_spend) — desbloqueados pela extensão do backend (FB + Google, grão diário).
// ────────────────────────────────────────────────────────────────────────────────────────────

// 1ª visita / marcação = qEEG Primeira (39) OU Psicologia (34) — mesma definição do dailyFirstVisitBookingsQuery.
export const FIRST_VISIT_SERVICES = [FIRST_EEG_SERVICE, PSY_SERVICE];

// Custo total (soma de amount_spent). Usado com filtro de dia (Custo total de marketing = hoje).
export const spendSumQuery: AggregateQuerySpec = {
    entity: "marketing_spend",
    aggregate: { sum: "amount_spent" },
    dataScope: "global",
};

// Custo mensal (Mkt Cost Per Goal): soma de amount_spent por mês da data de gasto.
export const spendMonthlyQuery: AggregateQuerySpec = {
    entity: "marketing_spend",
    aggregate: { sum: "amount_spent" },
    groupBy: ["year(date)", "month(date)"],
    dataScope: "global",
};

// Custo diário (chart semanal Leads-Marcações-Custos): soma de amount_spent por dia.
export const spendDailyQuery: AggregateQuerySpec = {
    entity: "marketing_spend",
    aggregate: { sum: "amount_spent" },
    groupBy: ["year(date)", "month(date)", "day(date)"],
    dataScope: "global",
};

// 1ªs avaliações marcadas por mês (MKT Results All): sessões svc39 com lead, por mês de criação (data de marcação).
export const firstEvalBookedMonthlyQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)"],
    fixedFilter: { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, { lead: { _nnull: true } }] },
    dataScope: "global",
};

// Leads que marcaram 1ª visita, por mês de criação (Marketing Efficiency: etapa leads→marcações).
// Réplica da fórmula Zoho evaluation_booked = progress in (evaluation_booked, evaluation_done).
export const leadsBookedMonthlyQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)"],
    fixedFilter: { progress: { _in: ["evaluation_booked", "evaluation_done"] } },
    dataScope: "global",
};

// Leads com avaliação FEITA, por mês de criação (cohort — MKT Results All "Evaluations").
// Réplica da fórmula Zoho evaluation_done_new = progress = 'evaluation_done'.
export const leadsEvaluatedMonthlyQuery: AggregateQuerySpec = {
    entity: "leads",
    aggregate: { count: "*" },
    groupBy: ["year(date_created)", "month(date_created)"],
    fixedFilter: { progress: { _eq: "evaluation_done" } },
    dataScope: "global",
};

export const EVALUATION_DONE_PROGRESS = "evaluation_done";

// "Por realizar" (Mkt Cost Per Goal): a 1ª avaliação ainda está marcada para hoje ou depois. O progress da
// lead não serve — fica em "evaluation_booked" depois de cancelamentos e faltas.
export const pendingFirstEvaluationLeadsFilter = (todayStart: string) => {
    const filter = {
        sessions: {
            _and: [
                { service: { _eq: FIRST_EEG_SERVICE } },
                { status: { _in: [...BOOKED_STATUSES, "in_session"] } },
                { start_date: { _gte: todayStart } },
            ],
        },
    };
    return filter;
};

// "Em aberto" (Mkt Cost Per Goal): tratamentos sem data de início com a decisão ainda pendente.
// "Não atenderam" fica de fora (contacto perdido).
export const OPEN_TREATMENT_STATUSES = ["waiting_report", "report_done", "thinking", "waiting_start_of_treatment", "all_good"];

// Avaliações feitas por mês E por clínica (chart mensal por clínica): svc39 done, por mês de início e clínica.
export const evaluationsDoneByClinicMonthlyQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(start_date)", "month(start_date)", "clinic_id"],
    fixedFilter: { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, { status: { _eq: "done" } }] },
    dataScope: "global",
};

// Clínicas apresentadas nas séries por clínica (Zoho mostra Porto/Braga; Virtual incluído por completude).
export const CLINIC_SERIES = [
    { id: 1, key: "porto", label: "Porto" },
    { id: 3, key: "braga", label: "Braga" },
    { id: 2, key: "virtual", label: "Virtual" },
];

// Filtro de datas próprio da tabela de grupos de anúncios (default: últimos 14 dias — pedido do Jorge).
// Ordenado do período mais curto para o mais longo, com "este mês"/"último mês" lado a lado.
export const AD_GROUP_DATE_PRESETS: Array<DateFilterKey> = [
    "hoje",
    "ultimos_7_dias",
    "ultimos_14_dias",
    "ultimos_30_dias",
    "este_mes",
    "ultimo_mes",
    "ultimos_3_meses",
    "ultimos_12_meses",
];
export const AD_GROUP_DEFAULT_PRESET: DatePresetKey = "ultimos_14_dias";

export const AD_GROUP_SOURCE_ORDER = ["Facebook", "Google", "Direct / no tracking"];

// Estado das leads Facebook por grupo de anúncios: estado da ÚLTIMA marcação (qualquer serviço) de cada lead.
// Cancelada inclui faltas e indisponibilidade nossa — sendo a última, não houve remarcação depois.
export const BOOKING_DONE_STATUSES = ["done", "in_session"];
export const BOOKING_CANCELLED_STATUSES = ["cancelled", "missed", "staff_unavailable"];
export const ADSET_STATUS_DEFAULT_PRESET: DatePresetKey = "ultimos_3_meses";
// Grupos de anúncios com 10 leads ou menos no período não aparecem (irrelevantes para a decisão — pedido do Jorge).
export const ADSET_STATUS_MIN_LEADS = 10;

// Gasto Facebook do período por registo marketing_adset (o nome resolve-se no cliente, para agrupar como as leads).
export const facebookSpendByAdsetQuery: AggregateQuerySpec = {
    entity: "marketing_spend",
    aggregate: { sum: "amount_spent" },
    groupBy: ["adset"],
    fixedFilter: { platform: { _eq: "facebook" } },
    dataScope: "global",
};

// Filtro de datas próprio do gráfico "Leads qualificadas por dia" (default: últimos 14 dias). Presets curtos
// porque o grão é diário — janelas de vários meses dariam pontos a mais para ler.
export const DAILY_LEADS_DATE_PRESETS: Array<DateFilterKey> = ["ultimos_7_dias", "ultimos_14_dias", "ultimos_30_dias", "este_mes"];
export const DAILY_LEADS_DEFAULT_PRESET: DatePresetKey = "ultimos_14_dias";

// ────────────────────────────────────────────────────────────────────────────────────────────
// COMPARAÇÃO CRIANÇAS vs ADULTOS — secção com filtro de datas próprio, sempre pela data de CRIAÇÃO da lead.
// ────────────────────────────────────────────────────────────────────────────────────────────

// Estados de sessão que contam como comparência e como falha do cliente. Ficam FORA do rácio de
// comparência: staff_unavailable (cancelamento nosso, não comportamento do cliente) e as sessões ainda
// não realizadas (booked/confirmed) — incluí-las faria os períodos recentes parecerem falsamente maus.
export const ATTENDED_SESSION_STATUSES = ["done", "in_session"];
export const FAILED_SESSION_STATUSES = ["cancelled", "missed"];

// Ordem das linhas do pivot (as etiquetas vêm de leadTypeFromClientType).
export const SEGMENT_ROW_ORDER = ["Criança", "Adulto", "Desconhecido"];

// A secção é um cohort pela data de criação da lead: uma lead precisa de MESES para marcar, comparecer e
// comprar, por isso o default é uma janela longa (12 meses). As janelas de 1 mês ficam disponíveis de
// propósito (leitura do fluxo recente de leads/marcações), mas nelas as colunas de compra assentam numa
// mão-cheia de vendas — o custo por venda dispara com 1 ou 2 compras. O warningNote do widget avisa que
// as colunas de compra dos períodos recentes são um mínimo. Não há presets de dias: seriam só demora.
export const SEGMENT_DATE_PRESETS: Array<DateFilterKey> = [
    "este_mes",
    "ultimo_mes",
    "ultimos_3_meses",
    "ultimos_6_meses",
    "ultimos_12_meses",
    "este_ano",
];
export const SEGMENT_DEFAULT_PRESET: DatePresetKey = "ultimos_12_meses";
